export interface Point2D {
  x: number;
  y: number;
}

export interface GeoCoord {
  lat: number;
  lng: number;
}

export interface Viewport {
  center: Point2D;
  scale: number;
}

/**
 * Projects a WGS84 coordinate (lat, lng) to a local Cartesian metric coordinate (x, y) in meters
 * relative to an anchor reference point (lat0, lng0) using the local tangent plane projection.
 * +x is East (meters), +y is North (meters).
 */
export function projectToLocalMeters(coord: GeoCoord, anchor: GeoCoord): Point2D {
  const R = 6371000; // Mean Earth radius in meters
  const rad = Math.PI / 180;
  const dLat = (coord.lat - anchor.lat) * rad;
  const dLng = (coord.lng - anchor.lng) * rad;
  const meanLat = ((coord.lat + anchor.lat) / 2) * rad;

  return {
    x: R * dLng * Math.cos(meanLat),
    y: R * dLat,
  };
}

/**
 * Converts a local Cartesian metric coordinate (x, y) back to WGS84 (lat, lng)
 * relative to the anchor reference point.
 */
export function localMetersToGeo(pt: Point2D, anchor: GeoCoord): GeoCoord {
  const R = 6371000;
  const rad = Math.PI / 180;
  const lat = anchor.lat + (pt.y / R) / rad;
  const meanLat = ((lat + anchor.lat) / 2) * rad;
  const lng = anchor.lng + (pt.x / (R * Math.cos(meanLat))) / rad;
  return { lat, lng };
}

/**
 * Converts a local metric coordinate (x, y) relative to the anchor into canvas screen pixel coordinates.
 * Screen center (width / 2, height / 2) corresponds to viewport center (center.x, center.y).
 * +x is East (screen right), +y is North (screen up, so canvas Y is inverted).
 */
export function metricToCanvas(
  pt: Point2D,
  center: Point2D,
  scale: number,
  width: number,
  height: number
): Point2D {
  return {
    x: width / 2 + (pt.x - center.x) * scale,
    y: height / 2 - (pt.y - center.y) * scale,
  };
}

/**
 * Converts canvas screen pixel coordinates back into local metric coordinates (x, y) in meters.
 */
export function canvasToMetric(
  canvasPt: Point2D,
  center: Point2D,
  scale: number,
  width: number,
  height: number
): Point2D {
  return {
    x: center.x + (canvasPt.x - width / 2) / scale,
    y: center.y - (canvasPt.y - height / 2) / scale,
  };
}

/**
 * Calculates optimal Web Mercator zoom level (integer in [10, 20])
 * based on the canvas scale (pixels per meter) and latitude.
 */
export function calculateZoomLevel(scale: number, latitude: number): number {
  if (scale <= 0) return 14;
  const latRad = (latitude * Math.PI) / 180;
  const earthCircumferenceAtLat = 40075016.686 * Math.cos(latRad);
  const metersPerPixel = 1 / scale;
  const idealZoom = Math.log2(earthCircumferenceAtLat / (256 * metersPerPixel));
  return Math.max(10, Math.min(20, Math.round(idealZoom)));
}

/**
 * Computes bounding box and optimal center & scale to fit a set of metric points
 * within a canvas of size (width, height) with specified padding.
 */
export function calculateFitViewport(
  points: Point2D[],
  width: number,
  height: number,
  padding = 40
): { center: Point2D; scale: number; bounds: { minX: number; maxX: number; minY: number; maxY: number } } {
  if (!points || points.length === 0) {
    return {
      center: { x: 0, y: 0 },
      scale: 1,
      bounds: { minX: -100, maxX: 100, minY: -100, maxY: 100 },
    };
  }

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }

  const rangeX = Math.max(50, maxX - minX);
  const rangeY = Math.max(50, maxY - minY);
  const usableWidth = Math.max(50, width - padding * 2);
  const usableHeight = Math.max(50, height - padding * 2);

  const scale = Math.min(usableWidth / rangeX, usableHeight / rangeY);
  const center = {
    x: (minX + maxX) / 2,
    y: (minY + maxY) / 2,
  };

  return { center, scale, bounds: { minX, maxX, minY, maxY } };
}

/**
 * Clamps a viewport (center & scale) to the "Fit Track" bounding box around the given track
 * metric points so the visible viewport edges never exceed the "Fit Track" bounds at any zoom level.
 */
export function clampViewport(
  viewport: Viewport,
  points: Point2D[],
  width: number,
  height: number,
  padding = 35
): Viewport {
  const fit = calculateFitViewport(points, width, height, padding);
  const minScale = Math.max(0.01, Math.min(30, fit.scale));
  const maxScale = 30;
  const scale = Math.max(minScale, Math.min(maxScale, viewport.scale));

  const spanX = Math.max(50, fit.bounds.maxX - fit.bounds.minX);
  const spanY = Math.max(50, fit.bounds.maxY - fit.bounds.minY);

  const fitHalfW = width > 0 ? width / 2 / minScale : spanX / 2;
  const fitHalfH = height > 0 ? height / 2 / minScale : spanY / 2;

  const halfVisW = width > 0 ? width / 2 / scale : 0;
  const halfVisH = height > 0 ? height / 2 / scale : 0;

  const maxOffsetX = Math.max(0, fitHalfW - halfVisW);
  const maxOffsetY = Math.max(0, fitHalfH - halfVisH);

  const x = Math.max(fit.center.x - maxOffsetX, Math.min(fit.center.x + maxOffsetX, viewport.center.x));
  const y = Math.max(fit.center.y - maxOffsetY, Math.min(fit.center.y + maxOffsetY, viewport.center.y));

  return {
    center: { x, y },
    scale,
  };
}

/**
 * Converts WGS84 coordinates to Web Mercator tile index.
 */
export function getTileCoords(lat: number, lng: number, zoom: number): { x: number; y: number } {
  const n = Math.pow(2, zoom);
  const x = Math.floor(((lng + 180) / 360) * n);
  const latRad = (lat * Math.PI) / 180;
  const y = Math.floor(
    ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n
  );
  return { x, y };
}

/**
 * Converts tile Y coordinate to WGS84 latitude.
 */
export function tileToLat(y: number, z: number): number {
  const n = Math.PI - (2 * Math.PI * y) / Math.pow(2, z);
  return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
}

/**
 * Converts tile X coordinate to WGS84 longitude.
 */
export function tileToLng(x: number, z: number): number {
  return (x / Math.pow(2, z)) * 360 - 180;
}

/**
 * Computes great-circle ground distance in meters between two WGS84 coordinates.
 */
export function haversineDistanceMeters(c1: GeoCoord, c2: GeoCoord): number {
  const R = 6371000;
  const rad = Math.PI / 180;
  const dLat = (c2.lat - c1.lat) * rad;
  const dLng = (c2.lng - c1.lng) * rad;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(c1.lat * rad) * Math.cos(c2.lat * rad) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Projects a 2D point p onto a line segment [a, b].
 */
export function projectPointToSegment(
  p: Point2D,
  a: Point2D,
  b: Point2D
): { proj: Point2D; t: number; distSq: number } {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;

  if (lenSq === 0) {
    const distSq = (p.x - a.x) ** 2 + (p.y - a.y) ** 2;
    return { proj: { ...a }, t: 0, distSq };
  }

  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));

  const proj: Point2D = {
    x: a.x + t * dx,
    y: a.y + t * dy,
  };

  const distSq = (p.x - proj.x) ** 2 + (p.y - proj.y) ** 2;
  return { proj, t, distSq };
}

/**
 * Finds the continuous point along polyline segments closest to the target point in local metric coordinates.
 */
export function findClosestPointOnPolyline(
  target: Point2D,
  points: Point2D[],
  minIndex = 0,
  maxIndex = points.length - 1
): { progressIndex: number; point: Point2D; distSq: number } {
  if (!points || points.length === 0) {
    return { progressIndex: 0, point: { x: 0, y: 0 }, distSq: Infinity };
  }
  if (points.length === 1) {
    const dx = points[0].x - target.x;
    const dy = points[0].y - target.y;
    return { progressIndex: 0, point: { ...points[0] }, distSq: dx * dx + dy * dy };
  }

  const start = Math.max(0, Math.min(minIndex, points.length - 2));
  const end = Math.max(start, Math.min(maxIndex - 1, points.length - 2));

  let bestProgress = start;
  let bestPoint: Point2D = { ...points[start] };
  let minDistSq = Infinity;

  for (let i = start; i <= end; i++) {
    const a = points[i];
    const b = points[i + 1];
    const { proj, t, distSq } = projectPointToSegment(target, a, b);
    if (distSq < minDistSq) {
      minDistSq = distSq;
      bestProgress = i + t;
      bestPoint = proj;
    }
  }

  return { progressIndex: bestProgress, point: bestPoint, distSq: minDistSq };
}

/**
 * Finds the index of the track point in points closest to target.
 */
export function findClosestTrackPointIndex(
  target: Point2D,
  points: Point2D[],
  minIndex = 0,
  maxIndex = points.length - 1
): number {
  if (points.length === 0) return 0;
  const start = Math.max(0, Math.min(minIndex, points.length - 1));
  const end = Math.max(start, Math.min(maxIndex, points.length - 1));

  let bestIdx = start;
  let minDistSq = Infinity;

  for (let i = start; i <= end; i++) {
    const p = points[i];
    const dx = p.x - target.x;
    const dy = p.y - target.y;
    const distSq = dx * dx + dy * dy;
    if (distSq < minDistSq) {
      minDistSq = distSq;
      bestIdx = i;
    }
  }

  return bestIdx;
}
